/**
 * useCartValidation — validation pre-checkout des items du panier.
 *
 * Extrait de CartCheckoutModal.jsx (Lot 8b §71 zone gelée §13/§46).
 * Gère : validationLoading, invalidItems + useEffect POST /cart/validate.
 *
 * 2 états extraits de CartCheckoutModal.jsx :
 *   - validationLoading (useState false)
 *   - invalidItems      (useState [])
 *
 * 1 effet extrait :
 *   - useEffect([step]) → POST /cart/validate quand step === STEP.REVIEW
 *
 * §9 DRY — responsabilité unique : validation serveur du contenu du panier.
 * §52 — la logique de normalisation `res?.invalid || []` est capturable en Node.
 *
 * ⚠️ Zone gelée §13/§46 : cet hook déclenche uniquement une LECTURE (validation).
 *    Aucun verrou, aucun paiement ne transite ici.
 */
import { useState, useEffect } from 'react';
import { axiosWithToken } from '../utils/functions/apiCallWithToken';
import { SERVER_URL as serverURL } from '../core/config';


/**
 * Éligibilité coupon (Studio §72) : la réponse `valid[]` porte, par item validé, un booléen
 * `couponEligible` (un coupon ne remise que les items vendus par Earniverse Studio). On en dérive
 * une map `couponEligibilityByKey` (`${itemType}:${itemId}` → boolean) consommée par la modal pour
 * afficher le message d'inéligibilité par ligne et recalculer le total remisé (display-only ; le
 * serveur reste la source de vérité — il ne remise que les items éligibles).
 *
 * @param {string}   step      — étape courante (STEP.REVIEW déclenche la validation)
 * @param {string}   STEP_REVIEW — valeur constante 'REVIEW'
 * @param {Array}    cartItems — liste des items du panier
 * @returns {{
 *   validationLoading: boolean,
 *   invalidItems: Array,
 *   couponEligibilityByKey: Object,  // map `${itemType}:${itemId}` → boolean (couponEligible)
 * }}
 */
export function useCartValidation(step, STEP_REVIEW, cartItems) {
  const [validationLoading, setValidationLoading] = useState(false);
  const [invalidItems, setInvalidItems] = useState([]);
  const [couponEligibilityByKey, setCouponEligibilityByKey] = useState({});

  useEffect(() => {
    if (step !== STEP_REVIEW || !cartItems || cartItems.length === 0) return;
    setValidationLoading(true);
    axiosWithToken(`${serverURL}/cart/validate`, 'post', {
      items: cartItems.map((i) => ({ itemType: i.itemType, itemId: i.itemId })),
    })
      .then((res) => {
        setInvalidItems(res?.invalid || []);
        const map = {};
        (res?.valid || []).forEach((v) => {
          if (!v || v.itemType == null || v.itemId == null) return;
          map[`${v.itemType}:${v.itemId}`] = v.couponEligible !== false;
        });
        setCouponEligibilityByKey(map);
      })
      .catch(() => { setInvalidItems([]); setCouponEligibilityByKey({}); })
      .finally(() => { setValidationLoading(false); });
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  return { validationLoading, invalidItems, couponEligibilityByKey };
}

export default useCartValidation;
